// React Data Grid Component
import { AllCommunityModule, ColDef, ColGroupDef, ColTypeDef, ModuleRegistry } from "ag-grid-community";
import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import React, { forwardRef, useMemo } from "react";

import { faMagnifyingGlass } from "@performyard/fontawesome/pro-regular-svg-icons";

import { PYBaseTableTheme } from "./BaseTableStyle";

import { Icon, Text } from "../../atoms";
import { Skeleton } from "../../atoms/Skeleton";
import DateCol from "./StandardColumns/DateCol";
import DropdownOptionCol from "./StandardColumns/DropdownOptionCol";
import EmployeeCol from "./StandardColumns/EmployeeCol";
import LinkCol from "./StandardColumns/LinkCol";
import ProgressCol from "./StandardColumns/ProgressCol";
import TextCol from "./StandardColumns/TextCol";
import "./Table.css";

// Register all Community features
ModuleRegistry.registerModules([AllCommunityModule]);

export type TableProps<TData> = Omit<AgGridReactProps<TData>, "columnDefs"> & {
	/**
	 * If true, the table will use autoHeight layout (table hugs content)
	 * If false or undefined, the table will use container height (scrollable)
	 */
	autoHeight?: boolean;
	columnDefs?: PYColumnDef<TData>[];
	emptyStateText?: string;
	/** Minimum height of the table wrapper (e.g. 356 or "356px"). Useful with autoHeight when few rows. */
	minHeight?: number | string;
};
export type PYColumnDefCol<TData> = Omit<ColDef<TData>, "field"> & {
	type?: "text" | "employee" | "date" | "link" | "dropdown" | "progress" | string | string[];
	field?: string;
};
export type PYColumnDef<TData> = PYColumnDefCol<TData> | ColGroupDef<TData>;

export type TableRef<TData> = AgGridReact<TData>;

const defaultColumnTypes: { [key: string]: ColTypeDef } = {
	text: TextCol,
	employee: EmployeeCol,
	date: DateCol,
	link: LinkCol,
	dropdown: DropdownOptionCol,
	progress: ProgressCol,
};

//set default column with sorting, filtering, resizing, and moveable to false
const defaultColumnDefs: ColDef = {
	sortable: false,
	filter: false,
	resizable: false,
	suppressMovable: true,
	cellRendererSelector: (params) => {
		// If data is empty object (loading state), show shimmer
		if (params.data && Object.keys(params.data).length === 0) {
			return { component: ShimmerCellRenderer };
		}
		return undefined;
	},
};
const TableComponent = <TData,>(props: TableProps<TData>, ref: React.ForwardedRef<AgGridReact<TData>>) => {
	const { autoHeight, emptyStateText = "No results found", getRowId, minHeight, ...otherProps } = props;
	const columnTypes = useMemo(
		() => ({
			...defaultColumnTypes,
			...props?.columnTypes,
		}),
		[props?.columnTypes]
	);

	const rowData = useMemo(() => {
		if (props.loading) {
			return Array.from({ length: 20 }, () => ({}) as TData);
		}
		return props.rowData;
	}, [props.loading, props.rowData]);

	const hasNoRowData = useMemo(() => {
		return !props.loading && (!rowData || rowData.length === 0);
	}, [rowData, props?.loading]);

	const minHeightCss =
		minHeight === undefined ? undefined : typeof minHeight === "number" ? `${minHeight}px` : minHeight;

	return (
		<div
			style={{
				...(minHeightCss !== undefined ? { minHeight: minHeightCss } : {}),
				height: hasNoRowData ? "356px" : autoHeight ? "auto" : "100%",
			}}
			className="py-table cp-ag-grid-table size-full bg-neutral-0"
		>
			<AgGridReact<TData>
				defaultColDef={defaultColumnDefs}
				suppressCellFocus={true}
				getRowId={props.loading ? () => `loading-${Math.random()}` : getRowId}
				noRowsOverlayComponent={() => (
					<div className="flex flex-col items-center justify-center gap-2">
						<div className="rounded-full bg-neutral-2 p-2">
							<Icon icon={faMagnifyingGlass} size={24} />
						</div>
						<Text size="paragraph-1" weight="semi-bold">
							{emptyStateText}
						</Text>
					</div>
				)}
				{...otherProps}
				columnDefs={props.columnDefs as (ColDef<TData> | ColGroupDef<TData>)[]}
				rowData={rowData}
				theme={PYBaseTableTheme}
				columnTypes={columnTypes}
				ref={ref}
				loadingOverlayComponent={() => <div />}
				// we don't want auto height when there is no row data, so we can control the height of teh empty state table and overlay
				domLayout={autoHeight && !hasNoRowData ? "autoHeight" : undefined}
				suppressRowHoverHighlight={true}
			/>
		</div>
	);
};

export * from "./BaseTableStyle";

// Export AG Grid types for consumers
export type {
	ColDef,
	ColGroupDef,
	ColTypeDef,
	ICellRendererParams,
	ValueFormatterParams,
	ValueGetterParams,
	CellClassParams,
	SortDirection,
	SortChangedEvent,
	IRowNode,
} from "ag-grid-community";
export type { AgGridReactProps } from "ag-grid-react";

export const Table = forwardRef(TableComponent) as <TData>(
	props: TableProps<TData> & { ref?: React.ForwardedRef<AgGridReact<TData>> }
) => React.ReactElement;

const ShimmerCellRenderer = () => {
	return (
		<div className="flex size-full min-h-0 items-center justify-start">
			<Skeleton />
		</div>
	);
};
